import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';

const ROWS_PER_PAGE = 25;

const CreditNoteList = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    creditNoteNo: '',
    dateFrom: '',
    dateTo: '',
    buyer: '',
    salesContract: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchCreditNotes();
    // eslint-disable-next-line
  }, [page, debouncedSearch, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      creditNoteNo: '',
      dateFrom: '',
      dateTo: '',
      buyer: '',
      salesContract: ''
    });
  };

  const fetchCreditNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: ROWS_PER_PAGE.toString(),
        search: debouncedSearch,
        start_date: filters.dateFrom,
        end_date: filters.dateTo,
        sort_by: 'credit_note_date',
        sort_order: 'DESC'
      });

      const response = await fetch(`${API_BASE_URL}/api/sales/credit-note-get?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setCreditNotes(data.data || []);
        setTotal(data.pagination?.total || 0);
      } else {
        throw new Error(data.error || 'Failed to fetch credit notes');
      }
    } catch (err) {
      console.error('Error fetching credit notes:', err);
      setError(err.message);
      toast.error('Failed to fetch credit notes');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCreditNote = (creditNoteId) => {
    navigate(`/credit-note/preview/${creditNoteId}`);
  };

  const handlePrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    const totalPages = Math.ceil(total / ROWS_PER_PAGE);
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Credit Note List</h2>
        <button
          type="button"
          onClick={() => navigate('/credit-note/new')}
          className="flex items-center gap-1 text-white bg-[#2691cc] border border-[#2691cc] px-3 py-1.5 rounded text-xs font-medium hover:bg-[#0e8ac2] transition"
        >
          <FaPlus />
          Add Credit Note
        </button>
      </div>

      {/* Search and Filters */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by credit note number, buyer name, sales contract, or particulars..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#10a5e9]"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 transition"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 p-3 bg-gray-50 rounded border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Credit Note No.</label>
                <input
                  type="text"
                  name="creditNoteNo"
                  value={filters.creditNoteNo}
                  onChange={handleFilterChange}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#10a5e9]"
                  placeholder="Filter by credit note number"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Date From</label>
                <input
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#10a5e9]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Date To</label>
                <input
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#10a5e9]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Buyer</label>
                <input
                  type="text"
                  name="buyer"
                  value={filters.buyer}
                  onChange={handleFilterChange}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#10a5e9]"
                  placeholder="Filter by buyer name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Sales Contract</label>
                <input
                  type="text"
                  name="salesContract"
                  value={filters.salesContract}
                  onChange={handleFilterChange}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#10a5e9]"
                  placeholder="Filter by sales contract"
                />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Credit Note No</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Date</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Sales Contract</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Buyer Name</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Buyer GST</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">CMA/CHA Name</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Issuing Authority</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Warehouse Name</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Warehouse Location</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State Code</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">KABL Business Name</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Particulars</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Amount</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">GST Amount</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Total Amount</th>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Type</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="17" className="px-3 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="17" className="px-3 py-8 text-center text-red-500">
                  Error: {error}
                </td>
              </tr>
            ) : creditNotes.length === 0 ? (
              <tr>
                <td colSpan="17" className="px-3 py-8 text-center">
                  <NoRecordFound />
                </td>
              </tr>
            ) : (
              creditNotes.map((creditNote) => (
                <tr key={creditNote.credit_note_id} className="hover:bg-gray-50">
                  <td className="px-3 py-1 font-semibold whitespace-nowrap">{creditNote.credit_note_number}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{formatDateDDMMYY(creditNote.credit_note_date)}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.sales_contract_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.buyer_name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.buyer_gst || '-'}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.cha_cma_name || '-'}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.issuing_authority_name || '-'}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.warehouse_name || '-'}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.warehouse_location || '-'}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.state_code || '-'}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.kabl_business_name || '-'}</td>
                  <td className="px-3 py-1">
                    <div 
                      className="truncate max-w-32 whitespace-nowrap" 
                      title={creditNote.particulars || '-'}
                    >
                      {creditNote.particulars || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-1 whitespace-nowrap">₹{parseFloat(creditNote.amount || 0).toLocaleString()}</td>
                  <td className="px-3 py-1 whitespace-nowrap">₹{parseFloat(creditNote.gst_amount || 0).toLocaleString()}</td>
                  <td className="px-3 py-1 whitespace-nowrap font-semibold">₹{parseFloat(creditNote.total_amount || 0).toLocaleString()}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{creditNote.check_cma_cha || '-'}</td>
                   <td className="px-3 py-1 whitespace-nowrap">
                    <button
                      onClick={() => handleViewCreditNote(creditNote.credit_note_id)}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="View Credit Note"
                    >
                      <FaEye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > ROWS_PER_PAGE && (
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing {((page - 1) * ROWS_PER_PAGE) + 1} to {Math.min(page * ROWS_PER_PAGE, total)} of {total} entries
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrev}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNext}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
      />
    </div>
  );
};

export default CreditNoteList;
